import h from 'hyperscript';
import checkIcon from '@disy/cadenza-icons/check.svg';

import { formatPercent } from 'cadenza/utils/format/format';
import { uniqueId } from 'cadenza/utils/unique-id';
import { icon } from 'cadenza/utils/icon/icon';

import i18n from './progress-bar.properties';
import './progress-bar.css';

import { assertUnreachable } from 'cadenza/utils/custom-error';

const COMPONENT_NAME = 'd-progress-bar';

/**
 * Represents a progress bar web component with a native progress-bar at the top, a label at the bottom left
 * and a value text label at the bottom right.
 * Value text label format depends on max attribute. If max attribute is set, then the format is "<N>/<max>",
 * otherwise the value is shown in percents "<X>%". The text label can show any text by supplying a function
 * to "valueTextFormatter".
 *
 * @example
 *   <!-- no max attribute given, range is from 0 to 1, value text label will be formatted to "60%" -->
 *   <d-progress-bar value="0.6" label="Percent progress bar" />
 * @example
 *   <!-- max attribute is given, range is from 0 to max=5, value text label will be formatted to "3/5" -->
 *   <d-progress-bar max="5" value="3" label="Done count progress bar" />
 * @example
 *   <!-- the text label by overwriting the valueTextFormatter function -->
 *   const progressBar = h('d-progress-bar');
 *   progressBar.valueTextFormatter = () => `${formatPercent(progressBar.value/progressBar.max}% (${progressBar.value} of ${progressBar.max})`;
 */

export type ProgressBarState = 'ACTIVE' | 'FAILED' | 'ABORTED' | 'SUCCESS';
function i18nProgressBarState (value: ProgressBarState) {
  return i18n(`label.state.{{${value}}}`);
}

export class ProgressBar extends HTMLElement {

  _progress: HTMLProgressElement;
  _label: HTMLElement;
  _labelText = '';
  _valueText: HTMLElement;
  _state: ProgressBarState = 'ACTIVE';

  _valueTextFormatter?: () => string;

  constructor () {
    super();
    const labelId = uniqueId();
    const stateLabelId = uniqueId();
    this._progress = h('progress.d-progress-bar--progress-bar', { attrs: { 'aria-labelledby': `${labelId} ${stateLabelId}` } });
    this._label = h('span.d-progress-bar--label', { id: labelId });
    this._valueText = h('span.d-progress-bar--valuetext',  { attrs: { 'aria-hidden': 'true' } });
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    this.innerHTML = '';
    this.append(
      this._progress,
      h('.d-progress-bar--labels-container', [ this._label, this._valueText ])
    );

    this.max = this.hasAttribute('max') ? Number(this.getAttribute('max')) : undefined;

    if (!this.label) {
      this.label = this.getAttribute('label') ?? '';
    }

    if (this.hasAttribute('value')) {
      this.value = Number(this.getAttribute('value'));
    }
  }

  get max () {
    return this._progress.hasAttribute('max') ? Number(this._progress.getAttribute('max')) : undefined;
  }

  set max (max) {
    if (max !== undefined) {
      this._progress.setAttribute('max', String(max));
    } else {
      this._progress.removeAttribute('max');
    }
  }

  set label (value) {
    this._labelText = value;
    this._updateLabel();
  }

  get label () {
    return this._label.textContent ?? '';
  }

  set state (value) {
    this.classList.remove(`is-${this._state.toLowerCase()}`);
    this._state = value;
    this.classList.add(`is-${this._state.toLowerCase()}`);
    this._updateLabel();
  }

  get state () {
    return this._state;
  }

  set value (value) {
    this._progress.value = value;
    this._valueText.textContent = this._formatValue(this.value);
  }

  get value () {
    return this._progress.value;
  }

  set valueTextFormatter (value) {
    this._valueTextFormatter = value;
    this._valueText.textContent = this._formatValue(this.value);
  }

  get valueTextFormatter () {
    return this._valueTextFormatter;
  }

  _formatValue (value: number) {
    if (this.valueTextFormatter) {
      return this.valueTextFormatter();
    }
    const max = this.max;
    return max !== undefined ? doneCountFormatter(value, max) : formatPercent(value);
  }

  _updateLabel () {
    const state = this._state;
    this._label.textContent = this._getLabelTextContent();

    if (state === 'SUCCESS') {
      this._label.append(icon(checkIcon));
    }
  }

  _getLabelTextContent (): string {
    switch (this._state) {
      case 'ACTIVE':
        return this._labelText;
      case 'ABORTED':
        return i18nProgressBarState('ABORTED');
      case 'FAILED': {
        const statusText = i18nProgressBarState('FAILED');
        return this._labelText ? `${this._labelText} ${statusText}` : statusText;
      }
      case 'SUCCESS':
        return this._labelText;
    }
    return assertUnreachable(this._state);
  }

}

function doneCountFormatter (value: number, max: number) {
  return `${value}/${max}`;
}

customElements.define(COMPONENT_NAME, ProgressBar);
